import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { api } from '@/lib/api';

interface User {
  id: string;
  username: string;
  email: string;
  token: string;
  tokenExpireTime: string;
  roles: string[];
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isContributor: boolean;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const STORAGE_KEY = 'tracker-auth';

function loadUser(): User | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const user = JSON.parse(stored) as User;
    // Check token expiration
    if (new Date(user.tokenExpireTime) < new Date()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return user;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(loadUser);

  const isAuthenticated = user !== null;
  const isAdmin = user?.roles.includes('Admin') ?? false;
  const isContributor = user?.roles.includes('Contributor') ?? false;

  // Keep API token in sync
  useEffect(() => {
    if (user?.token) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    }
  }, [user]);

  const login = useCallback(async (username: string, password: string) => {
    const response = await api.post('auth/login', {
      json: { username, password },
    }).json<{ token: string; expiration: string; userId: string; username: string; email: string; roles: string[] }>();

    const newUser: User = {
      id: response.userId,
      username: response.username,
      email: response.email,
      token: response.token,
      tokenExpireTime: response.expiration,
      roles: response.roles,
    };
    setUser(newUser);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newUser));
  }, []);

  const register = useCallback(async (username: string, email: string, password: string) => {
    await api.post('auth/register', {
      json: { username, email, password },
    }).json();
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isAdmin, isContributor, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}

export function getStoredToken(): string | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const user = JSON.parse(stored) as User;
    if (new Date(user.tokenExpireTime) < new Date()) return null;
    return user.token;
  } catch {
    return null;
  }
}
